'use client';
import { useId as useNewsletterId } from 'react';
import { CaptchaRequestMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchBanners } from '@eliadmin/core/src/api/general/general-api';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';

export function Newsletter() {
  const seoNewsletterId = useNewsletterId();
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();
  const locale = useLocaleStore((s) => s.currentLocale);

  const { data: banners } = useQuery({
    queryKey: [CacheKey.BANNERS, 'banners'],
    queryFn: () => fetchBanners({ locale, query: { where: { type: { equals: 'banner' } } } }),
  });

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => setIsSubscribed(true),
  });

  if (!banners?.data) return null;

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <section id="newsletters" className={cn('my-5', 'ea-homepage-newsletter')}>
      <form
        className={cn('grid grid-cols-12 gap-6 py-2', 'ea-homepage-newsletter__form')}
        onSubmit={handleSubmit}
      >
<CaptchaRequestMessage error={subscribeMutation.error} />
        <div
          className={cn(
            'col-span-12 flex h-[220px] w-full flex-row items-center justify-center rounded-none md:col-span-6 md:h-full md:rounded-[4px]',
            'ea_newsletter_banner_container',
          )}
        >
          {banners.data.map((banner) => (
            <div
              className={cn(
                'h-full w-full min-w-full flex-1 md:min-w-[calc(50%-16px)]',
                'ea-banner-grid__container_newsletter',
              )}
              key={`${banner.url}-${banner.image.url}-${banner.image.name}`}
            >
              <Link href={banner.url ?? '#'} className={cn('ea-banner-grid__link_newsletter')}>
                <Image
                  alt={resolveImageAlt(banner?.image, banner?.name, 'image')}
                  src={banner.image.url}
                  width="0"
                  height="0"
                  sizes="100vw"
                  className={cn(
                    'h-full max-h-[220px] min-h-full w-full object-contain sm:max-h-none',
                    'ea-homepage-banner-grid__link-image_newsletter',
                  )}
                />
              </Link>
            </div>
          ))}
        </div>
        <div
          className={cn(
            'col-span-12 flex h-full flex-col justify-center gap-5 rounded-none bg-primary p-5 sm:max-h-[215px] md:col-span-6 md:rounded-[4px] md:p-10',
            'ea-homepage-newsletter__form-item-email',
          )}
        >
          <h2 className="text-center text-[24px] font-bold text-white md:text-[28px]">
            Odoberajte novinky a získajte skvelé zľavy!
          </h2>
          <div className={cn('flex h-10 flex-row items-center')}>
            <Input
              aria-label={systemLabels('email')}
              id={`newsletter-email-${seoNewsletterId}`}
              type="email"
              name="email"
              placeholder={t('text183')}
              onChange={(e) => setEmail(e.target.value)}
              className={{
                wrapper: cn(
                  ' rounded-[4px] border-none bg-input pr-1 text-muted-foreground ',
                  'ea-homepage-newsletter__form-item-email-input',
                ),
                main: 'rounded-[4px]',
              }}
              afterOuter={
                <Button
                  type="submit"
                  className={cn(
                    'ml-2  rounded-[4px] border-none bg-[#fecf33] px-4 text-primary-foreground hover:bg-[#ffe54c] ',
                    'ea-homepage-newsletter__form-item-email-submit-button',
                  )}
                  disabled={email.length === 0 || !checked || isSubscribed}
                  isLoading={subscribeMutation.isPending}
                >
                  {t('text182')}
                </Button>
              }
            />
          </div>
          {isSubscribed && <span className={cn('text-success')}>{t('text234')}</span>}
          <div
            className={cn(
              'flex flex-row items-center space-x-2',
              'ea-homepage-newsletter__form-item-gdpr',
            )}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => setChecked(!checked)}
              id={`newsletter-gdpr-${seoNewsletterId}`}
              aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
              className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
              required
            />
            <Label
              htmlFor={`newsletter-gdpr-${seoNewsletterId}`}
              className={cn('text-white', 'ea-homepage-newsletter__form-item-gdpr-checkbox-label')}
            >
              <span className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-span')}>
                {t('text264')}&nbsp;
                <a
                  href={t('text391')}
                  className={cn('ea-homepage-newsletter__form-item-gdpr-checkbox-link1')}
                >
                  {t('text265')}
                </a>{' '}
                <a
                  href={t('text392')}
                  className={cn('ea-homepage-newsletter__form-item-gdpr-checkbox-link2')}
                >
                  {t('text436')}
                </a>
                .
              </span>
            </Label>
          </div>
        </div>
      </form>
    </section>
  );
}
